import { useFormikContext } from 'formik'
import { useEffect, useState } from 'react'
import { defaultGUIDValue } from '@user/components/Dashboard/widgets/utils/constants'
import { searchMatchingRule } from '@user/store/leadAssignmentRule'
import type { ILeadFormModel } from '@user/components/Leads/AddLeadForm/typings'
import { selectCurrentUser } from '@user/store/authorization'
import { useSelector } from 'react-redux'
import type { LeadAssignmentRule } from '@user/components/Settings/LeadAssignmentRule/LeadAssignmentRuleForm/typings'
import { useD2DTerritoriesEnabled } from '@user/store/tenantFeatures/hooks/useD2DTerritoriesEnabled'

function entryIsNotEmpty([, value]) {
  return (
    (!!value && !Array.isArray(value)) || (Array.isArray(value) && value.length)
  )
}

export function useSearchAssignmentRule(isOverriden: boolean) {
  const { userId } = useSelector(selectCurrentUser)
  const d2dTerritoriesEnabled = useD2DTerritoriesEnabled()
  const [assignmentRule, setAssignmentRule] =
    useState<LeadAssignmentRule | null>(null)

  const [hasRemoteAssignmentRule, setHasRemoteAssignmentRule] =
    useState<boolean>(false)
  // sourceId is intentionally not read: the add lead form has no such property
  const {
    values: {
      postalCode,
      branchId,
      campaignId,
      divisionId,
      type,
      locationTypeId,
      state,
      leadSourceId,
      targetPests,
      assignmentRuleFieldFocused,
      lat,
      lng,
    },
  } = useFormikContext<
    ILeadFormModel & { assignmentRuleFieldFocused?: boolean }
  >()

  // Coordinates are an input to the territory lookup and to nothing else, so a tenant without
  // D2D Territories should neither send them nor re-run this search when they change. Held as
  // their own values rather than inlined, so the dependency list can name the gated form.
  const territoryLat = d2dTerritoriesEnabled ? lat : undefined
  const territoryLng = d2dTerritoriesEnabled ? lng : undefined

  useEffect(() => {
    if (isOverriden || assignmentRuleFieldFocused) {
      setAssignmentRule(null)
    } else {
      let mounted = true

      // Coordinates ride through entryIsNotEmpty below, which drops falsy values. That is correct
      // and must not be "fixed": ILeadFormModel initialises lat/lng to 0, so 0 means "no location
      // yet", not the equator -- sending it would run every address-typed lead's territory lookup
      // against a point off West Africa.
      const query = {
        ZipCode: postalCode,
        BranchId: branchId,
        CampaignId: campaignId,
        DivisionId: divisionId,
        CreatedById: userId,
        LeadTypeId: type,
        LocationTypeId: locationTypeId,
        StateAbbreviation: state,
        leadSourceId,
        targetPest: targetPests,
        Latitude: territoryLat,
        Longitude: territoryLng,
      }

      const preparedQuery = Object.fromEntries(
        Object.entries(query).filter(entryIsNotEmpty),
      )

      try {
        const execute = async () => {
          const rule: LeadAssignmentRule =
            await searchMatchingRule(preparedQuery)

          if (mounted) {
            if (rule) {
              setAssignmentRule({
                ...rule,
                ownerId:
                  rule.ownerId === defaultGUIDValue
                    ? 'unassigned'
                    : rule.ownerId,
              })
            } else {
              setAssignmentRule(null)
            }
            setHasRemoteAssignmentRule(!!rule)
          }
        }
        execute()
        return () => {
          mounted = false
        }
      } catch (e) {
        console.log('ERROR: ', e)
        setAssignmentRule(null)
      }
    }
  }, [
    postalCode,
    campaignId,
    divisionId,
    userId,
    type,
    locationTypeId,
    state,
    leadSourceId,
    setAssignmentRule,
    targetPests,
    isOverriden,
    branchId,
    assignmentRuleFieldFocused,
    // Without these the territory lookup goes stale: nudging the Add Lead pin within the same
    // postal code changes the coordinates but no other listed dependency, so the effect would
    // never re-run and the old location's owners would stick (WWM-17508).
    //
    // The GATED copies, not lat/lng themselves. A tenant without D2D Territories has no use for
    // the coordinates, and listing the raw values made every geocode result and every pin nudge
    // re-run the search for them too. Each run calls setAssignmentRule with a fresh object, and
    // the Assignment effect reapplies the rule wholesale -- so a rep who had picked a different
    // funnel, stage or approved owner watched the choice revert the moment the address resolved.
    territoryLat,
    territoryLng,
  ])

  return { assignmentRule, hasRemoteAssignmentRule, isOverriden }
}
